import { useCallback, useEffect, useState } from "react";
import "./Manager.css";
import Dashboard from "../../components/Dashboard";
import EditInvite from "../../components/EditInvite";
import ManagerTabs from "../../components/ManagerTabs";
import SeatingArrangements from "../../components/SeatingArrangements";
import Settings from "../../components/Settings";
import EventDetailsModal from "../../components/EventDetailsModal";
import AutopilotSetup from "../../components/AutopilotSetup";
import CommercePurchaseReturn from "../../components/CommercePurchaseReturn";
import { DEMO_READ_ONLY_MESSAGE, getDocument, isDemoDocument } from "../../firebase";
import { getMissingEventDetailKeys, getStoredEventDetails } from "../../Utils/eventDetails";
import { isPackageEvent } from "../../Utils/recordLimit";
import WhatsAppSupportButton from "../../components/WhatsAppSupportButton";


const managerTabs = ["dashboard", "edit", "messages", "autopilot", "seating", "settings"];

const getInitialTab = () => {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return managerTabs.includes(tab) ? tab : "dashboard";
};

const Manager = ({ addAlertText,docID }) => {
  const [state, setState] = useState(getInitialTab);
  const [showGuestPreview, setShowGuestPreview] = useState(false);
  const [eventDetailsData, setEventDetailsData] = useState(null);
  const [eventDetailsReady, setEventDetailsReady] = useState(false);
  const [detailsRevision, setDetailsRevision] = useState(0);
  const isDemo = isDemoDocument(docID);
  const packageEvent = isPackageEvent(eventDetailsData || {});
  const visibleState = eventDetailsReady
    ? (packageEvent && state === "messages" ? "autopilot" : (!packageEvent && state === "autopilot" ? "messages" : state))
    : state;

  // The soft design (design-refresh.css + design-soft.css) applies to the
  // owner panel only; guest pages, login and admin keep their own look.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("ds-refresh", "ds-soft");
    return () => root.classList.remove("ds-refresh", "ds-soft");
  }, []);

  const showDemoReadOnlyMessage = () => {
    addAlertText({ text: DEMO_READ_ONLY_MESSAGE, mode: "warning" });
  };

  useEffect(() => {
    if (!docID) {
      setEventDetailsReady(true);
      return undefined;
    }

    let active = true;
    getDocument({ docID })
      .then((document) => {
        if (!active) return;
        setEventDetailsData(document?.data?.() || {});
      })
      .catch(() => {
        if (!active) return;
        addAlertText({ text: "לא הצלחנו לטעון את פרטי האירוע.", mode: "danger" });
      })
      .finally(() => {
        if (active) setEventDetailsReady(true);
      });

    return () => {
      active = false;
    };
  }, [addAlertText, docID]);

  useEffect(() => {
    if (!eventDetailsReady || visibleState === state) return;
    setState(visibleState);
  }, [eventDetailsReady, state, visibleState]);

  useEffect(() => {
    if (!isDemo) return undefined;

    const handleBlockedWrite = (event) => {
      addAlertText({
        text: event.detail?.message || DEMO_READ_ONLY_MESSAGE,
        mode: "warning",
      });
    };
    window.addEventListener("demo-read-only-attempt", handleBlockedWrite);
    return () => window.removeEventListener("demo-read-only-attempt", handleBlockedWrite);
  }, [addAlertText, isDemo]);

  useEffect(() => {
    if (!showGuestPreview) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowGuestPreview(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [showGuestPreview]);

  const blockDemoFieldInteraction = (event) => {
    if (!isDemo) return;
    const editableField = event.target.closest?.("input, textarea, select, [contenteditable='true']");
    if (!editableField) return;
    if (
      editableField.dataset.demoAllow === "true" ||
      editableField.closest?.("[data-demo-allow='true']")
    ) return;
    if (event.type === "keydown" && event.key === "Tab") return;

    event.preventDefault();
    event.stopPropagation();
    editableField.blur?.();
    showDemoReadOnlyMessage();
  };

  const storedEventDetails = getStoredEventDetails(eventDetailsData || {});
  const shouldCompleteEventDetails = Boolean(
    eventDetailsReady &&
    eventDetailsData &&
    !isDemo &&
    getMissingEventDetailKeys(storedEventDetails).length > 0
  );

  const handleEventDetailsSaved = (savedDetails) => {
    setEventDetailsData((current) => ({
      ...(current || {}),
      title: savedDetails.title,
      eventDetails: {
        date: savedDetails.date,
        time: savedDetails.time,
        venueName: savedDetails.venueName,
        address: savedDetails.address,
      },
      invitationData: {
        ...(current?.invitationData || {}),
        locationName: savedDetails.venueName,
        locationAddress: savedDetails.address,
        withWazeLink: savedDetails.withWazeLink,
      },
    }));
    setDetailsRevision((current) => current + 1);
  };

  const refreshEventDetailsData = useCallback(async () => {
    if (!docID) return;
    const document = await getDocument({ docID });
    setEventDetailsData(document?.data?.() || {});
    setDetailsRevision((current) => current + 1);
  }, [docID]);

  return (
    <div className="manager-page col-12 d-flex flex-column position-relative justify-content-center align-items-center" id="navbar">

      <CommercePurchaseReturn eventId={docID} onFulfilled={refreshEventDetailsData} />

      <ManagerTabs
        activeTab={visibleState}
        onTabClick={setState}
        onPreview={() => setShowGuestPreview(true)}
        previewDisabled={!docID}
        packageEvent={packageEvent}
        packageStatusReady={eventDetailsReady && Boolean(eventDetailsData)}
      />
      {showGuestPreview && (
        <div
          className="guest-preview-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowGuestPreview(false);
          }}
        >
          <section className="guest-preview-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="guest-preview-title">
            <header className="guest-preview-modal__header">
              <div>
                <strong id="guest-preview-title">תצוגת ההזמנה לאורח</strong>
                <span>כך ההזמנה תיראה בקישור האורחים</span>
              </div>
              <button type="button" onClick={() => setShowGuestPreview(false)} aria-label="סגירת התצוגה">×</button>
            </header>
            <div className="guest-preview-modal__device">
              <iframe
                src={`/invitation/${docID}?preview=manager`}
                title="תצוגה מקדימה של ההזמנה לאורח"
              />
            </div>
          </section>
        </div>
      )}
      {shouldCompleteEventDetails && (
        <EventDetailsModal
          docID={docID}
          initialDetails={storedEventDetails}
          originalInvitationData={eventDetailsData.invitationData}
          watermarkManaged={eventDetailsData.invitationDelivery?.deliveryMode === "FREE_WATERMARK"}
          addAlertText={addAlertText}
          onSaved={handleEventDetailsSaved}
        />
      )}
      {eventDetailsReady && !isDemo && (
        <AutopilotSetup
          docID={docID}
          visible={visibleState === "autopilot"}
          isReadOnly={isDemo}
          addAlertText={addAlertText}
          refreshKey={`${detailsRevision}:${visibleState}`}
        />
      )}
      <div
        className="col-12 d-flex justify-content-center align-items-center"
        onPointerDownCapture={blockDemoFieldInteraction}
        onKeyDownCapture={blockDemoFieldInteraction}
      >
        {eventDetailsReady && visibleState !== "autopilot" && (
          <View
            key={`${visibleState}-${detailsRevision}`}
            state={visibleState}
            setState={setState}
            addAlertText={addAlertText}
            docID={docID}
            isReadOnly={isDemo}
            eventAccessData={eventDetailsData}
            onPackageAssignmentChanged={(autopilotAssignment, recordLimit, rsvpAccess) => setEventDetailsData((current) => ({
              ...(current || {}),
              autopilotAssignment,
              recordLimit,
              rsvpAccess,
            }))}
          />
        )}
      </div>
      {/* Gift banner (styled by design-soft.css). Opens in a new window;
          a normal followed link (no rel="nofollow"). */}
      <a
        className="manager-gift-banner"
        href="https://www.flashback.co.il/he/rsvps/%D7%9E%D7%AA%D7%A0%D7%95%D7%AA-%D7%91%D7%90%D7%A9%D7%A8%D7%90%D7%99/"
        target="_blank"
        rel="noopener"
      >
        <img
          src="https://ucarecdn.com/e5a3f0f8-8d79-403d-9533-8991c7826b86/noroot.png"
          alt="מתנות באשראי - 100% מהמתנה נשארת אצלכם, והשליחה של הלינק עלינו"
        />
      </a>
      <a className="pointer text-dark p-2 border" href="/manual">לחצו כאן להדרכה ועזרה</a>
      <WhatsAppSupportButton />

    </div>
  );
};

export default Manager;
const View = ({ state, setState, addAlertText,docID, isReadOnly, eventAccessData, onPackageAssignmentChanged }) => {
  if (state === "dashboard") {
    return <Dashboard addAlertText={addAlertText} docID={docID} isReadOnly={isReadOnly} onOpenAutopilot={() => setState("autopilot")} onPackageAssignmentChanged={onPackageAssignmentChanged} />;
  }
  if (state === "edit") {
    return <EditInvite addAlertText={addAlertText} docID={docID} isReadOnly={isReadOnly} />;
  }
  if (state === "seating") {
    return <SeatingArrangements addAlertText={addAlertText} docID={docID} isReadOnly={isReadOnly} />;
  }
  if (state === "messages" || state === "settings") {
    return <Settings addAlertText={addAlertText} docID={docID} activeTab={state} onTabChange={setState} isReadOnly={isReadOnly} eventAccessData={eventAccessData} />;
  }
};
